import React, { useEffect, useMemo, useState } from "react";
import {
  getReportStatus,
  getVideoStatus,
  type ReportStatusResponse,
  type VideoStatusResponse,
} from "@/api/reportApi";
import ReportComponent from "@/modules/report/Report";

interface ReportProps {
  id?: string;
}

type FeedbackRow = { criteria: string; rating: number | string; remarks: string };
type ProviderProfile = {
  full_name?: string;
  username?: string;
  profile_image_url?: string;
  email?: string;
};

const POLL_INTERVAL_MS = 5000;
const PROVIDER_ENDPOINT =
  (typeof import.meta !== "undefined" && (import.meta as any)?.env?.PROVIDER_ENDPOINT) ||
  (typeof process !== "undefined" && (process.env as any)?.PROVIDER_ENDPOINT) ||
  "https://employability.ai";

const normalizePerformanceHighlight = (input: any) => {
  const normalizeItems = (
    items: Array<{ criteria?: string; category?: string; rating?: number; score?: number; remarks?: string; feedback?: string }>
  ) =>
    items
      .filter(Boolean)
      .map((item) => ({
        criteria: item.criteria || item.category || "Performance",
        rating: item.rating ?? item.score ?? "-",
        remarks: item.remarks || item.feedback || "",
      }));

  if (Array.isArray(input) && input.length > 0) {
    const normalizedItems = normalizeItems(input);
    const first = normalizedItems[0];
    const numericRatings = normalizedItems
      .map((item) => (typeof item.rating === "number" ? item.rating : null))
      .filter((val): val is number => val !== null);
    const averageRating =
      numericRatings.length > 0
        ? numericRatings.reduce((sum, value) => sum + value, 0) / numericRatings.length
        : 0;

    return {
      rating: typeof first?.rating === "number" ? first.rating : averageRating,
      category: first?.criteria || "Highlight",
      remarks: first?.remarks || "",
      items: normalizedItems,
    };
  }

  if (input && typeof input === "object") {
    const normalizedItems = Array.isArray((input as any).items)
      ? normalizeItems((input as any).items)
      : undefined;
    const numericRatings = normalizedItems
      ?.map((item) => (typeof item.rating === "number" ? item.rating : null))
      .filter((val): val is number => val !== null);
    const averageRating =
      numericRatings && numericRatings.length > 0
        ? numericRatings.reduce((sum, value) => sum + value, 0) / numericRatings.length
        : 0;

    return {
      rating: input.rating ?? input.score ?? averageRating ?? 0,
      category: input.criteria || input.category || normalizedItems?.[0]?.criteria || "Highlight",
      remarks: input.remarks || input.feedback || normalizedItems?.[0]?.remarks || "",
      ...(normalizedItems ? { items: normalizedItems } : {}),
    };
  }

  return null;
};

const mapFeedbackRows = (feedbackData: any): FeedbackRow[] => {
  if (!feedbackData) return [];
  const feedbackItems = Array.isArray(feedbackData.feedbackItems)
    ? feedbackData.feedbackItems
    : [];

  const feedbackRows = feedbackItems.map((item: any, idx: number) => {
    const strengths = Array.isArray(item.strengths)
      ? `Strengths: ${item.strengths.join(", ")}`
      : "";
    const improvements = Array.isArray(item.improvements)
      ? `Improvements: ${item.improvements.join(", ")}`
      : "";

    const remarks = [item.feedback, strengths, improvements]
      .filter(Boolean)
      .join(" | ");

    return {
      criteria:
        item.message ||
        item.question ||
        `Response ${item.messageIndex ?? idx + 1}`,
      rating: item.score ?? "-",
      remarks,
    };
  });

  if (feedbackRows.length) return feedbackRows;

  const questionRows = Array.isArray(feedbackData.questionsAsked)
    ? feedbackData.questionsAsked.map((item: any, idx: number) => {
        const ideal =
          typeof item.idealResponse === "string"
            ? item.idealResponse
            : item.idealResponse?.description;
        const bestExample =
          typeof item.idealResponse === "object" &&
          item.idealResponse?.bestResponse
            ? `Best: ${item.idealResponse.bestResponse}`
            : null;
        const remarks = [item.remarks, ideal, bestExample]
          .filter(Boolean)
          .join(" | ");

        return {
          criteria: item.question || `Question ${idx + 1}`,
          rating: item.rating ?? item.score ?? "-",
          remarks,
        };
      })
    : [];

  return questionRows;
};

const Report: React.FC<ReportProps> = ({ id }) => {
  const [report, setReport] = useState<ReportStatusResponse | null>(null);
  const [videoStatus, setVideoStatus] = useState<VideoStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [providerProfile, setProviderProfile] = useState<ProviderProfile | null>(null);
  const [providerProfileUrl, setProviderProfileUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError("No report ID provided");
      setLoading(false);
      return;
    }

    let isCancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const fetchReport = async (isPoll = false) => {
      try {
        const [reportData, videoData] = await Promise.all([
          getReportStatus(id),
          getVideoStatus(id),
        ]);

        if (isCancelled) return;

        setReport(reportData);
        setVideoStatus(videoData);

        const isReportComplete = reportData.generationStatus === "completed";
        const isVideoDone =
          videoData.isMerged ||
          videoData.statusOfRecording === "completed" ||
          videoData.statusOfRecording === null;

        if (isReportComplete && isVideoDone && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch report");
      } finally {
        if (!isPoll) {
          setLoading(false);
        }
      }
    };

    fetchReport(false);
    intervalId = setInterval(() => fetchReport(true), POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [id]);

  useEffect(() => {
    const email = report?.interviewDetails?.user_email;

    if (!email) {
      setProviderProfile(null);
      setProviderProfileUrl(null);
      return;
    }

    let isCancelled = false;

    const fetchProfile = async () => {
      try {
        const response = await fetch(
          `${PROVIDER_ENDPOINT}/api/v1/user/getUserByEmail?email=${encodeURIComponent(email)}`
        );
        if (!response.ok) return;

        const payload = (await response.json()) as {
          success?: boolean;
          data?: ProviderProfile;
        };

        if (!payload?.success || !payload.data || isCancelled) return;

        setProviderProfile(payload.data);
        setProviderProfileUrl(
          payload.data.username
            ? `${PROVIDER_ENDPOINT}/profile/${payload.data.username}`
            : null
        );
      } catch {
        // best-effort; ignore profile fetch errors
      }
    };

    fetchProfile();

    return () => {
      isCancelled = true;
    };
  }, [report?.interviewDetails?.user_email]);

  const formattedReport = useMemo(() => {
    const extractResources = (input: any): any[] => {
      if (Array.isArray(input)) return input;
      if (Array.isArray(input?.resources)) return input.resources;
      if (Array.isArray(input?.data?.resources)) return input.data.resources;
      return [];
    };

    const coreData = (report?.coreReport?.data as any) ?? {};
    const feedbackData = (report?.feedback?.data as any) ?? {};
    const interviewDetails = report?.interviewDetails;
    const usernameFromData =
      providerProfile?.username ??
      coreData.profile?.username ??
      interviewDetails?.user_email;
    const profileUrlFromUsername = usernameFromData
      ? `${PROVIDER_ENDPOINT}/profile/${usernameFromData}`
      : undefined;
    const profile =
      (providerProfile
        ? {
            name:
              providerProfile.full_name ??
              providerProfile.username ??
              interviewDetails?.user_name,
            username:
              providerProfile.username ??
              providerProfile.email ??
              interviewDetails?.user_email,
            profile_picture: providerProfile.profile_image_url,
            profile_url:
              profileUrlFromUsername ??
              (providerProfileUrl ?? undefined),
          }
        : null) ||
      (coreData.profile
        ? {
            name:
              coreData.profile?.name ??
              coreData.profile?.full_name ??
              coreData.profile?.username,
            username: coreData.profile?.username ?? coreData.profile?.email,
            profile_picture: coreData.profile?.profile_picture,
            profile_url:
              profileUrlFromUsername ??
              coreData.profile?.profile_url,
          }
        : undefined) ||
      (interviewDetails
        ? {
            name: interviewDetails.user_name,
            username: interviewDetails.user_email,
            profile_url: profileUrlFromUsername,
          }
        : undefined);
    const feedbackQuestions = Array.isArray(feedbackData.questionsAsked)
      ? feedbackData.questionsAsked
      : [];
    const coreQuestions = Array.isArray(coreData.questionsAsked)
      ? coreData.questionsAsked
      : [];
    const mergedQuestions = feedbackQuestions.length
      ? feedbackQuestions
      : coreQuestions.length
        ? coreQuestions
        : [];
    const interviewMessages = Array.isArray(report?.interviewMessages)
      ? report.interviewMessages.map((msg) => ({
          _id: msg.id,
          role: msg.role,
          message: msg.message,
          isInstructionMessage: Boolean(msg.isInstruction),
          isToolCall: false,
        }))
      : [];
    const transcriptMessages = interviewMessages.length > 0 ? interviewMessages : [];
    const performanceHighlight = normalizePerformanceHighlight(
      coreData.performance_highlights
    );
    const resourcesService = (report as any)?.resources;
    const resourcesFromCore = extractResources(coreData.resources);
    const resourcesFromService = extractResources(resourcesService);
    const resources =
      resourcesFromCore.length > 0 ? resourcesFromCore : resourcesFromService;
    const resourcesStatus =
      (typeof coreData.resources === "object" && !Array.isArray(coreData.resources)
        ? coreData.resources?.status
        : undefined) ||
      (typeof resourcesService === "object" && !Array.isArray(resourcesService)
        ? resourcesService?.status
        : undefined);
    const feedbackRows = mapFeedbackRows(feedbackData);

    return {
      data: {
        ...coreData,
        interviewId: report?.interviewId,
        interviewDetails,
        profile,
        resources,
        resourcesStatus,
        performance_highlights:
          performanceHighlight ?? coreData.performance_highlights,
        response_feedback: feedbackRows.length
          ? feedbackRows
          : mergedQuestions.length
            ? mergedQuestions
            : coreData.response_feedback,
        questionsAsked: mergedQuestions,
        messages: transcriptMessages,
      },
      profile,
      s3_recording_url: videoStatus?.mergedUrl ? [videoStatus.mergedUrl] : [],
      recording: videoStatus
        ? {
            mergedUrl: videoStatus.mergedUrl,
            statusOfRecording: videoStatus.statusOfRecording,
          }
        : undefined,
    };
  }, [report, videoStatus, providerProfile, providerProfileUrl]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6f8fb]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Fetching the latest report…</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6f8fb]">
        <div className="text-center">
          <p className="text-red-500 mb-4">Error: {error ?? "No report found"}</p>
          <p className="text-gray-500">Please verify the report ID and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#f6f8fb]">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-10">
        <ReportComponent report={formattedReport} />
      </div>
    </div>
  );
};

export default Report;
